import { db, auth } from "./firebase";
import { ref, set, get, update, onValue, off, runTransaction } from "firebase/database";
import { recordUpdate, isPlaybackLocked } from "./replay"; // H6 — the three writes below ARE the action log
import { STARTING_ANIMA, MAX_ANIMA, STARTING_KHAO,
         // R08's WINDOW and R08's PAYLOAD TEST. Both are pure and both
         // already live in rules.js; see THE R08 SEAL below for why the
         // backstop is HERE and not only in the renderer.
         isOpeningTurnIndex, adversaryDamageInPayload,
         // R110 — the width of the battle line. Four since 2026-09-07.
         ESCLING_SLOTS } from "./rules";

// ── The rifts a seat opens with ───────────────────────────────
// ONE builder, both seats. The shape is identical to what the two
// call sites hand-wrote before, which is the point: the only thing
// that changed is that `escling` can now arrive already chosen.
// A malformed or short triple pads with nulls rather than throwing
// — a Set is player-authored data and must never be able to
// prevent a match from being created.
// ⚠ R110 — THIS IS WHERE A REAL MATCH SEAT IS BORN, so the width of the
// battle line is decided here for every PvP duel in the game. It read
// [0, 1, 2] until 2026-09-07; three here would have meant a three-wide
// duel no matter what the board rendered, and the Mind rift would simply
// never have existed. Driven by the constant now, so the next change to
// the line is one number in rules.js.
const seatEsclings = (chosen) => {
  const pick = Array.isArray(chosen) ? chosen : [];
  return Array.from({ length: ESCLING_SLOTS }, (_, i) => ({
    id: `slot${i}`,
    escling: (typeof pick[i] === "string" && pick[i]) ? pick[i] : null,
    escendant: null,
    soul: [],
  }));
};

// ── Generate a 4 digit invite code ───────────────────────────
export const generateCode = () => {
  return Math.floor(1000 + Math.random() * 9000).toString();
};

// ── Create a new match ────────────────────────────────────────
// MP-FIX: codes are only 4 digits (9000 combos) and matches are never
// deleted, so a fresh code could collide with a LIVE match — and set()
// would obliterate it mid-game. We now probe up to 20 codes and only
// accept one that is free, finished, or stale (>24h old).
const CODE_STALE_MS = 24 * 60 * 60 * 1000;
// ⭐ THE COOLING PERIOD (2026-09-05). A `finished` match is NOT immediately
// recyclable. The result screen still reads it, App.js's Gauntlet carry
// reads `players/0/anima` off it AFTER the duel ends, and replay.js commits
// its tape from it. Recycling the instant `status` flips let a brand-new
// host set() over a node three other readers were still holding. A finished
// code now has to go cold first.
const CODE_COOLDOWN_MS = 5 * 60 * 1000;
const codeIsDead = (m) => {
  if (!m) return true;
  const age = Date.now() - (m.createdAt || 0);
  if (age > CODE_STALE_MS) return true;
  if (m.status !== "finished") return false;
  const finishedAt = m.finishedAt || m.createdAt || 0;
  return (Date.now() - finishedAt) > CODE_COOLDOWN_MS;
};

// TWO PASSES, and the order is the whole point. Pass 1 accepts only a code
// that does not exist at all — the one case `matches/$code`'s write rule
// waves through unconditionally (`!data.exists()`). Pass 2 recycles a dead
// node, which a NON-participant is not guaranteed to be allowed to
// overwrite under the hardened rule, so it is the fallback and never the
// first choice. Returns { code, recycled } so the caller can pick its write.
const findFreeCode = async () => {
  for (let i = 0; i < 20; i++) {
    const candidate = generateCode();
    const snap = await get(ref(db, `matches/${candidate}`));
    if (!snap.exists()) return { code: candidate, recycled: false };
  }
  for (let i = 0; i < 10; i++) {
    const candidate = generateCode();
    const snap = await get(ref(db, `matches/${candidate}`));
    if (!snap.exists()) return { code: candidate, recycled: false };
    if (codeIsDead(snap.val())) return { code: candidate, recycled: true };
  }
  return null; // astronomically unlikely — DB nearly full of live matches
};

// ── ONE seat, for any seat index ──────────────────────────────
// The exact shape createMatch/joinMatch always wrote, factored out so
// a 3rd/4th seat is "call this again," not a second hand-written copy
// that can drift from the first. `team`/`controller` are new, optional
// fields — absent on a classic 1v1 seat, which is what keeps every
// existing match read/write in the app byte-compatible with what it
// already expects.
const buildSeat = (myUid, playerName, grimoire, luxmoire, prophecy, hand, curios, esclings, seatOpts = {}) => {
  const seat = {
    uid: myUid,
    name: playerName,
    anima: STARTING_ANIMA,
    maxAnima: MAX_ANIMA,
    khao: STARTING_KHAO,
    maxKhao: STARTING_KHAO,
    grimoire: grimoire || {},
    luxmoire: luxmoire || {},
    prophecy: prophecy || null,
    hand: hand || [],
    field: {
      esclings: seatEsclings(esclings),
      tomes: [null, null, null],
      curios: curios || [null, null],
      prophecy: prophecy || null,
    },
    necropolis: [],
    // ── R112/R113 · THE EXILE RIFT AND ITS MARK ─────────────
    // Seeded explicitly rather than left to every reader's `|| []`, for
    // the same reason the four Anima ledgers below are: a field that is
    // sometimes absent is a field every consumer has to defend against,
    // and one of them always forgets.
    //   exile         — the rift. Cards here are out of the Solrend.
    //   exileReturned — ids that have spent their ONE permitted return.
    //                   A card on this list is routed to `exile` rather
    //                   than `necropolis` when ruined, can never be
    //                   Resurrected by any method, and can never be
    //                   retrieved again.
    exile: [],
    exileReturned: [],
    shadowGamblingStreak: 0,
    // R100 — TWO ledgers. totalAnimaLost/animaLostThisTurn is the
    // ADVERSARIAL one (the only one a Prophecy may read);
    // totalAnimaSelfDimmed/animaSelfDimmedThisTurn is what the seat did
    // to itself. Both seeded so a seat's first half-turn reads the same
    // as every later one.
    totalAnimaLost: 0,
    animaLostThisTurn: 0,
    totalAnimaSelfDimmed: 0,
    animaSelfDimmedThisTurn: 0,
    animaGainedThisTurn: 0,
    animaGainedLastTurn: 0,
    ready: true,
  };
  if (seatOpts.controller) seat.controller = seatOpts.controller; // "human" | "ai" — absent = human, unchanged from before
  if (seatOpts.team != null) seat.team = seatOpts.team;
  return seat;
};

// ── THE N-SEAT LAYER — opts is new and entirely optional. Every
// existing call site (App.js, ChallengeSystem.js, the Gauntlet, etc.)
// calls createMatch/joinMatch with the old seven positional args and
// gets EXACTLY the old two-seat match back — `opts` defaulting to {}
// means `seatCount` defaults to 2, `teams`/`sharedAnimaTeams` stay
// absent, and the written shape is identical to what these two
// functions wrote before this file changed. New multiplayer modes are
// the only callers that ever pass a populated opts.
//
//   opts.seatCount        — 2 (default), 3, or 4
//   opts.mode             — "1v1" | "ffa3" | "ffa4" | "2v2" | "2v2ranked"
//                            | "gauntlet2v2" | "story-coop" | ... — a
//                            label, never switched on internally here
//                            (THE EXCEPTION LAW — a string the caller
//                            and the UI read, not an enum this file
//                            branches on)
//   opts.teams            — { [seatIdx]: teamId }, e.g. {0:"A",1:"B",
//                            2:"A",3:"B"} for interleaved 2v2 seating
//   opts.sharedAnimaTeams — true for 2v2 (caP'n: "they share Anima and
//                            not Khao") — teamed seats pool one Anima
//                            total instead of each keeping their own
//   opts.deckMode         — "canon" (must pass RulesEnforcer's
//                            validateDeckLanes/validateDeckLimits) or
//                            "open" (default — unchanged from today,
//                            no deck gate at all)
//   opts.entranceFee      — { amount, currency: "chocolateCoins" } —
//                            Tournament of Kings buy-in; absent = free
//   opts.hostControllers  — { [seatIdx]: "ai" } to seed AI-controlled
//                            seats at creation (Gauntlet/co-op story
//                            reuse THIS SAME match shape with the
//                            opposing seats bot-controlled instead of
//                            standing up a second engine)
//   opts.aiDecks          — { [seatIdx]: { grimoire, luxmoire, prophecy,
//                            hand, curios, esclings } } — the ACTUAL
//                            deck an AI seat plays (a Gauntlet rung's
//                            boss, a story villain), keyed the same as
//                            hostControllers. An "ai" seat with no
//                            entry here falls back to a copy of the
//                            host's own deck, which is never correct
//                            for a real opponent — every real caller
//                            is expected to pass one.
export const createMatch = async (playerName, grimoire, luxmoire, prophecy, hand, curios = [null, null], esclings = null, opts = {}) => {
  // VACUUM-SEAL: a real account is required to host. We stamp the host's
  // uid onto seat 0 so the DB rules can verify only the two seated players
  // ever write to this match.
  const myUid = auth.currentUser && auth.currentUser.uid;
  if (!myUid) throw new Error("You must be signed in to host a match.");

  const seatCount = Number.isInteger(opts.seatCount) && opts.seatCount >= 2 && opts.seatCount <= 4 ? opts.seatCount : 2;
  const hostSeat = buildSeat(myUid, playerName, grimoire, luxmoire, prophecy, hand, curios, esclings, {
    team: opts.teams ? opts.teams[0] : undefined,
    controller: opts.hostControllers ? opts.hostControllers[0] : undefined,
  });

  const players = { 0: hostSeat };
  for (let i = 1; i < seatCount; i++) {
    const ctrl = opts.hostControllers ? opts.hostControllers[i] : undefined;
    // An AI-controlled seat (Gauntlet-2v2, co-op story) is filled at
    // creation time, not waited on — there's no second human to join it.
    //
    // opts.aiDecks lets the CALLER hand this seat its own grimoire —
    // a Gauntlet boss or a story villain's actual deck, not a second
    // copy of the host's. Without an entry there, it falls back to the
    // host's own deck/hand/curios/esclings — a harmless default for a
    // caller that genuinely has no separate deck to give it, but never
    // the right answer for a real opponent, so every real call site
    // (Gauntlet-2v2, co-op story) is expected to pass one.
    const aiDeck = opts.aiDecks?.[i];
    players[i] = ctrl === "ai"
      ? buildSeat(
          null, opts.aiNames?.[i] || "The Gauntlet",
          aiDeck?.grimoire ?? grimoire, aiDeck?.luxmoire ?? luxmoire, aiDeck?.prophecy ?? prophecy,
          aiDeck?.hand ?? hand, aiDeck?.curios ?? curios, aiDeck?.esclings ?? esclings,
          { team: opts.teams ? opts.teams[i] : undefined, controller: "ai" }
        )
      : null;
  }

  const initialState = {
    code: null,          // stamped by the claim loop below, once a code is won
    status: "waiting",
    createdAt: Date.now(),
    winner: null,
    turn: 0,
    activePlayer: 0,
    seatCount,
    mode: opts.mode || "1v1",
    deckMode: opts.deckMode || "open",
    solrendReady: allSeatsFalse(seatCount),
    players,
  };
  if (opts.teams) initialState.teams = opts.teams;
  if (opts.sharedAnimaTeams) {
    initialState.sharedAnimaTeams = true;
    initialState.teamAnima = buildTeamAnima(opts.teams, seatCount, STARTING_ANIMA);
  }
  // THE BRAIN'S BRIEF (2026-09-06) — difficulty + story ids for an AI
  // seat, stored on the match so BOTH human clients can read it at
  // exit and the host can attach the brain with the right skill.
  if (opts.aiMeta && typeof opts.aiMeta === "object") initialState.aiMeta = opts.aiMeta;
  if (opts.entranceFee?.amount > 0) {
    initialState.entranceFee = { amount: opts.entranceFee.amount, currency: opts.entranceFee.currency || "chocolateCoins" };
    initialState.pot = { [myUid]: opts.entranceFee.amount };
  }

  // Every seat is filled already (an AI-only-remainder match, e.g.
  // Gauntlet-2v2/co-op story with one human) — go active immediately,
  // same as joinMatch does the instant the last human seat lands.
  if (allSeatsFilled(players, seatCount)) {
    initialState.status = "active";
  }

  // ⭐ THE ATOMIC CLAIM (2026-09-05). This was `await set(matchRef, state)`.
  // `findFreeCode` probes with get() and the claim was a blind set(), so two
  // hosts creating inside the same round-trip both saw 4711 free and both
  // wrote it — the second one OBLITERATING the first host's entire match
  // node. The exact disaster findFreeCode's own banner says it exists to
  // prevent, left open on the one write that causes it. joinMatch has used a
  // transaction for this class of race since the day it was written; the
  // creation half just never caught up.
  //
  // The transaction is the claim. If someone won the code between the probe
  // and the write, the updater sees a live node and aborts, and we go get
  // another code rather than trampling theirs.
  for (let attempt = 0; attempt < 4; attempt++) {
    const found = await findFreeCode();
    if (!found) throw new Error("No free match codes — try again in a moment.");
    const candidate = found.code;
    const candidateRef = ref(db, `matches/${candidate}`);
    const state = { ...initialState, code: candidate };
    let res;
    try {
      res = await runTransaction(candidateRef, (current) => {
        if (current && !codeIsDead(current)) return; // abort — someone beat us here
        return state;
      });
    } catch (e) {
      // A recycled node can be refused by the DB rules (a non-participant
      // overwriting somebody else's finished match is not something the
      // hardened rule promises to allow). Treat it exactly like a lost
      // race: take another code rather than surfacing a permission error.
      continue;
    }
    if (res.committed) return candidate;
  }
  throw new Error("Could not claim a match code — try again in a moment.");
};

const allSeatsFalse = (seatCount) => {
  const o = {};
  for (let i = 0; i < seatCount; i++) o[i] = false;
  return o;
};

const allSeatsFilled = (players, seatCount) => {
  for (let i = 0; i < seatCount; i++) if (!players[i]) return false;
  return true;
};

// One shared Anima total per team, seeded to `base` (30, same starting
// value every seat already opens with) — so a 2v2 team's pooled life
// starts equal to what either teammate would have had alone, not
// double it.
const buildTeamAnima = (teams, seatCount, base) => {
  const out = {};
  for (let i = 0; i < seatCount; i++) {
    const t = teams ? teams[i] : null;
    if (t != null && out[t] == null) out[t] = base;
  }
  return out;
};

// ── Join an existing match ─────────────────────────────────────
// MP-FIX: slot claim is now a TRANSACTION. The old read-check-write had a
// race — two joiners could both pass the "players[1] empty" check and the
// second write silently hijacked the first player's seat.
//
// Generalized to N seats: claims the first OPEN seat (any of them, not
// just seat 1) so a 3p/4p lobby fills in whatever order players arrive.
// Pass `seatHint` to claim a specific seat (e.g. a team-balanced 2v2
// lobby assigning new joiners to whichever team is short a player);
// left null, the transaction claims the lowest open index.
export const joinMatch = async (code, playerName, grimoire, luxmoire, prophecy, hand, curios = [null, null], esclings = null, opts = {}) => {
  // VACUUM-SEAL: a real account is required to join. We stamp the joiner's
  // uid onto their seat so the DB rules can verify only seated players
  // ever write to this match.
  const myUid = auth.currentUser && auth.currentUser.uid;
  if (!myUid) return { error: "You must be signed in to join a match." };

  const matchRef = ref(db, `matches/${code}`);
  const snapshot = await get(matchRef);

  if (!snapshot.exists()) return { error: "Match not found." };

  const match = snapshot.val();
  if (match.status !== "waiting") return { error: "Match already in progress." };

  // ⭐ ONE PLAYER, ONE CHAIR (2026-09-05). Nothing ever checked whether this
  // uid was ALREADY seated. A host could open a second tab, type their own
  // code, and claim seat 1 — a 2-seat match instantly flipped to "active"
  // and they duelled themselves; a 3p FFA became two humans across three
  // chairs. The VACUUM-SEAL comment below claims the rules "verify only the
  // seated players ever write", and it is right — a uid seated twice
  // satisfies that check for BOTH seats, which is exactly why the rules
  // could never catch this and the client has to.
  if (alreadySeated(match.players, seatCountOfRaw(match), myUid)) {
    return { error: "You are already seated in this match." };
  }

  const seatCount = match.seatCount || 2;
  const seatHint = Number.isInteger(opts.seatHint) ? opts.seatHint : null;

  // ⭐ THE CLAIM IS ONE SEAT, NOT THE WHOLE TABLE (2026-09-06). This was a
  // transaction on `matches/{code}` that rewrote the entire node with one
  // more seat in it. Under the hardened rule a non-participant may write
  // ONLY `players/{idx}` — an empty chair, stamped with their own uid,
  // while the match is still `waiting` — and nothing else, so the old
  // root write is refused for every joiner. (The old rule instead let any
  // signed-in stranger rewrite every field but the four uids: anima,
  // hands, `winner`, the pooled totals. That is the hole this closes.)
  //
  // Per-seat transaction: the updater sees the chair; if somebody sat in
  // it between our read and our write, it aborts and we read again and
  // pick another chair. `status === "waiting"` and "not already seated"
  // are checked from the read on every attempt and ENFORCED by the rule
  // on the write, so a race cannot seat anyone into a live duel.
  let claimedIdx = null;
  let latest = match;
  for (let attempt = 0; attempt < 4; attempt++) {
    if (attempt > 0) {
      const again = await get(matchRef);
      if (!again.exists()) return { error: "Match not found." };
      latest = again.val();
      if (latest.status !== "waiting") return { error: "Match already in progress." };
      if (alreadySeated(latest.players, seatCountOfRaw(latest), myUid)) return { error: "You are already seated in this match." };
    }
    const sc = latest.seatCount || seatCount;
    const idx = seatHint != null
      ? (latest.players?.[seatHint] ? null : seatHint)
      : bestOpenSeat(latest, sc);
    if (idx == null) return { error: "Match is full." };
    const seatForIdx = buildSeat(myUid, playerName, grimoire, luxmoire, prophecy, hand, curios, esclings, {
      team: latest.teams ? latest.teams[idx] : undefined,
    });
    let res;
    try {
      res = await runTransaction(ref(db, `matches/${code}/players/${idx}`), (current) => {
        if (current) return;          // somebody sat here first — abort, re-read
        return seatForIdx;
      });
    } catch (e) {
      // A refusal from the rules. Said plainly, not as "full": until the
      // repo's rules.json is deployed the live rule may not know the
      // per-seat door, and "Match is full" would send caP'n hunting
      // seats instead of deploying.
      if (e?.code === "PERMISSION_DENIED" || /permission/i.test(String(e?.message || ""))) {
        return { error: "The Empire refused the seat — rules may not be deployed." };
      }
      continue;
    }
    if (res.committed) { claimedIdx = idx; break; }
  }
  if (claimedIdx == null) return { error: "Match is full." };
  // Note the board for the R08 seal, same as every other read path.
  noteBoardForR08(code, latest);

  // STEP TWO — now that this uid owns a seat, the participant branch of the
  // write rule grants full access, so the status flip is legal. Best-effort
  // on purpose: a match sitting full-but-"waiting" is cosmetic, never a
  // block. Nothing in the duel gates on `status === "active"` (the ceremony
  // gates on seats + presence), and `ensureActive` below lets any seated
  // client repair it. Only `status === "finished"` is load-bearing.
  try {
    const fresh = (await get(matchRef)).val();
    if (fresh && fresh.status === "waiting" && allSeatsFilled(fresh.players, fresh.seatCount || 2)) {
      await update(matchRef, { status: "active", activePlayer: fresh.activePlayer ?? 0 });
    }
  } catch (e) { /* cosmetic — see above */ }

  return { success: true, seatIdx: claimedIdx };
};

// A seated client can repair a match left full-but-"waiting" by a step-two
// that never landed (dropped socket, tab closed mid-join). Safe to call on
// every match mount: it is a no-op for anything already active or finished.
export const ensureActive = async (code) => {
  try {
    const matchRef = ref(db, `matches/${code}`);
    const m = (await get(matchRef)).val();
    if (!m || m.status !== "waiting") return;
    if (!allSeatsFilled(m.players, m.seatCount || 2)) return;
    await update(matchRef, { status: "active", activePlayer: m.activePlayer ?? 0 });
  } catch (e) { /* best effort */ }
};

const seatCountOfRaw = (m) => (Number.isInteger(m?.seatCount) ? m.seatCount : 2);
const alreadySeated = (players, seatCount, uid) => {
  if (!uid) return false;
  for (let i = 0; i < seatCount; i++) if (players?.[i]?.uid === uid) return true;
  return false;
};

// ── COMING BACK ─────────────────────────────────────────────────────
// ⭐ (2026-09-05) There was no way back into your own seat. Seat identity
// lived only in React state — no localStorage, no re-entry — so a refresh,
// a phone locking, or a browser crash mid-duel booted you to the Plaza,
// and the only door back was `joinMatch`, which refuses anything that is
// not still "waiting":
//
//     if (match.status !== "waiting") return { error: "Match already in progress." };
//
// Your seat object — your uid, your hand, your Grimoire, your whole board
// — sat in `matches/{code}/players/{idx}` fully intact and unreachable,
// while your heartbeat stopped and 45 seconds later your adversary was
// offered your Solrend. Everything needed to come back was already stored;
// nothing ever asked. This asks: is one of this match's seats MINE?
export const findMySeat = async (code) => {
  const myUid = auth.currentUser && auth.currentUser.uid;
  if (!myUid || !code) return null;
  let m;
  try { m = (await get(ref(db, `matches/${code}`))).val(); }
  catch (e) { return null; }
  if (!m) return null;
  const sc = m.seatCount || 2;
  for (let i = 0; i < sc; i++) {
    if (m.players?.[i]?.uid === myUid) {
      return { seatIdx: i, status: m.status, finished: m.status === "finished", match: m };
    }
  }
  return null;
};

// The one door that handles both cases, so no caller has to know which it
// is looking at: an unclaimed chair is a JOIN, a chair already stamped with
// my uid is a RESUME, and a finished match is neither.
export const joinOrResume = async (code, playerName, grimoire, luxmoire, prophecy, hand, curios = [null, null], esclings = null, opts = {}) => {
  const mine = await findMySeat(code);
  if (mine && !mine.finished) return { success: true, seatIdx: mine.seatIdx, resumed: true };
  if (mine && mine.finished) return { error: "That Solrend is over." };
  return joinMatch(code, playerName, grimoire, luxmoire, prophecy, hand, curios, esclings, opts);
};

const firstOpenSeat = (players, seatCount) => {
  for (let i = 0; i < seatCount; i++) if (!players?.[i]) return i;
  return null;
};
// ⭐ THE PARTNER SITS BESIDE YOU (2026-09-06). 2v2 seats are interleaved
// {0:A, 1:B, 2:A, 3:B}, and the first open chair after the host is seat
// 1 — team B. "Bring a partner" therefore seated the partner ACROSS the
// table. In a team match the open seat chosen is the one on the team
// with the fewest seated players (ties → lowest index), so the first
// joiner lands beside the host, and the second pair fills the other
// side. A caller with an explicit seatHint still gets that seat.
const bestOpenSeat = (match, seatCount) => {
  const players = match?.players;
  const teams = match?.teams;
  if (!teams) return firstOpenSeat(players, seatCount);
  const filledByTeam = {};
  for (let i = 0; i < seatCount; i++) {
    if (players?.[i] && teams[i] != null) filledByTeam[teams[i]] = (filledByTeam[teams[i]] || 0) + 1;
  }
  let best = null, bestCount = Infinity;
  for (let i = 0; i < seatCount; i++) {
    if (players?.[i]) continue;
    const c = teams[i] == null ? Infinity : (filledByTeam[teams[i]] || 0);
    if (c < bestCount) { best = i; bestCount = c; }
  }
  return best != null ? best : firstOpenSeat(players, seatCount);
};

// ── Get match once ────────────────────────────────────────────
export const getMatch = async (code) => {
  const matchRef = ref(db, `matches/${code}`);
  const snapshot = await get(matchRef);
  const m = snapshot.exists() ? snapshot.val() : null;
  noteBoardForR08(code, m);   // see THE R08 SEAL — this read is free
  return _overlayPooledAnima(m);   // defined further down; module-level, so evaluated before any call
};

// ── Listen to match changes in real time ──────────────────────
export const listenToMatch = (code, callback) => {
  const matchRef = ref(db, `matches/${code}`);
  // onValue returns an unsubscribe function — use it directly
  // This allows multiple independent listeners on the same path
  const unsub = onValue(matchRef, (snapshot) => {
    if (!snapshot.exists()) return;
    const m = snapshot.val();
    noteBoardForR08(code, m);   // see THE R08 SEAL — this read is free
    callback(_overlayPooledAnima(m));
  });
  // Return the specific unsubscribe, not off() which kills ALL listeners
  return unsub;
};

// ── Update game state ─────────────────────────────────────────
// ── H6 · THE THREE TAPS ───────────────────────────────────────
// Every board mutation in Terror funnels through these three
// functions, and every one of them is already a Firebase update
// payload — an ordered diff of the complete truth. So the replay
// recorder taps here and nowhere else, and there is no second
// description of the game that could drift from the first.
//
// recordUpdate is a no-op unless a recording is live for THIS code.
// It is synchronous, allocation-light, and cannot throw — a replay
// must never be able to break a duel.

// ════════════════════════════════════════════════════════════════════
// ⚔ THE R08 SEAL — AT THE ONE FIREBASE TOUCHPOINT
// ════════════════════════════════════════════════════════════════════
// caP'n, 2026-08-25: *"neither can Attack at all first turn"*, where
// Attack is the broad word — *"a tome or Impera or anything to 'attack'
// a being or your adversary directly"*.
//
// WHY IT MOVED DOWN HERE. R08's backstop was written in Shatterfield.js,
// on that file's local `updatePlayer` shadow, and its banner claims it
// is "the ONE function every adversary write in this file already goes
// through". Every adversary write IN THAT FILE — which is the hole. It
// bound on NONE of:
//   · NPC.js — the brain has its own write overseer and no R08 clause
//     in it; the tome executor, the Soul-card dispatcher and section
//     10's offensive Tome destruction were all ungated.
//   · prophecyEngine.js, turnEngine.js, matchOutcome.js, bossPhases.js.
// Measured: in shipping 1v1 the brain places Tomes face-down on its
// first Turn and makes ZERO adversary writes during the opening Cycle,
// so it was LATENT — but a hand-built board with a face-up aggressive
// Tome at turn 1 produces a live violation, and "latent" is not a rule.
//
// THIS FILE IS THE ONLY PLACE EVERY WRITER MEETS. Shatterfield.js has
// never called Firebase directly and neither has anything else: the
// engine, the brain, the renderer and the harness all arrive here. A
// rule enforced at one chokepoint cannot be forgotten at the twentieth,
// and this is the last chokepoint there is.
//
// ── THE COST, AND WHY IT IS NEARLY NOTHING ─────────────────────────
// The seal needs three things: is it the opening Cycle, who is acting,
// and what did the victim's seat look like BEFORE. Reading the match on
// every write to answer that would double the read traffic of the whole
// game. So the decision to look is separated from the verdict:
//
//   • `_r08Watch` is a per-code note of the last known turn/seatCount,
//     fed from data that ALREADY flows through this module — every
//     getMatch, every listener snapshot, every createMatch/joinMatch,
//     and every updateMatch carrying a `turn`. It is only ever used to
//     decide WHETHER TO LOOK, so a stale note costs at most one extra
//     read (it finds no damage) or fails open exactly the way the
//     engine already did.
//   • The VERDICT is computed from a FRESH read, taken only when the
//     note says the opening Cycle is running and the target is somebody
//     else's seat. Outside that window — i.e. for the entire rest of
//     every duel — this block is one Map lookup and a comparison.
//
// ⚠ MEASURED, not estimated. tools/balance.mjs, --seed 19 --games 40,
// with and without the seal: 56,260 reads → 56,961. SEVENTEEN extra
// reads per duel, all of them inside the opening Cycle, and ZERO extra
// writes. The batch produced no refusals at all, which is the other
// half of the proof — a backstop that fires during legal play is not a
// backstop, it is a bug.
//
// ── A REFUSAL IS LOUD, AND IT IS A BACKSTOP, NOT THE RULE ──────────
// Reaching here means a route got to damage without passing a gate, so
// it says so on the console with the payload that tried it. A silent
// backstop is a bug farm.
//
// ── THE DECLARED EXCEPTION ─────────────────────────────────────────
// `opts.notAnAttack` — a string naming why this adversary-side write is
// not an Attack. Exactly one shape needs it: a COMPLETED DEVOTION
// CONTRACT, where the payer sends the finished Tome to ITS OWN OWNER'S
// Necropolis (Shatterfield.handleDevote, NPC.npcHonourDevotions). That
// payload is byte-identical to a ruin and no predicate can tell them
// apart, so the writer declares it and the declaration is logged. It is
// a DECLARATION, not a bypass: it is greppable, it names a rule, and
// anything using it that is not a contract is a defect visible in one
// search.
// Bounded by construction: one entry per match code, and a code is four
// digits (generateCode above), so this Map can never hold more than
// 9,000 two-field objects however long a client stays open.
const _r08Watch = new Map();   // code -> { turn, seatCount }
// ── THE POOL NOTE (2026-09-06) ──────────────────────────────────────
// A second per-code note, fed from the same free reads: does this
// match pool Anima, and which seat sits on which team. It exists so
// updatePlayer below can redirect a per-seat `anima` write into the
// team's pool WITHOUT a read on every write — the same trick the R08
// note uses, and for the same reason. `null` = not yet known.
const _poolWatch = new Map();  // code -> { shared:boolean, teams:{idx:teamId}|null }
const _notePool = (code, m) => {
  if (!code || !m || typeof m !== "object") return;
  if (!("sharedAnimaTeams" in m) && !("teams" in m)) return;   // a partial payload — nothing to learn
  const key = String(code);
  const had = _poolWatch.get(key) || { shared: false, teams: null };
  _poolWatch.set(key, {
    shared: "sharedAnimaTeams" in m ? !!m.sharedAnimaTeams : had.shared,
    teams:  "teams" in m ? (m.teams || null) : had.teams,
  });
};
// MERGES, never replaces: a payload that carries a `turn` and no
// `seatCount` (which is every hand-off write) must not silently reset a
// four-seat table to two.
export const noteBoardForR08 = (code, m) => {
  if (!code || !m) return;
  _notePool(code, m);
  const key = String(code);
  const had = _r08Watch.get(key) || {};
  const turn = typeof m.turn === "number" ? m.turn : had.turn;
  if (typeof turn !== "number") return;
  const seatCount = Number(m.seatCount) || had.seatCount || 2;
  _r08Watch.set(key, { turn, seatCount });
};

// ── THE LIFE A SEAT ACTUALLY HAS, ON EVERY READ (2026-09-06) ────────
// Under `sharedAnimaTeams` a team's whole life lives at
// `teamAnima/{t}`, and the per-seat `anima` field is a fossil sitting
// at its opening value. Shatterfield overlays the pool by hand
// (withEffectiveAnima) at its own read sites; NOTHING ELSE DID. The
// NPC brain reads `players[0].anima` to decide whether a blow is
// lethal and `players[NPC_IDX].anima` to decide whether it can afford
// a fare — so in a pooled match (Gauntlet — Duo, Story Co-op) the
// brain saw two seats frozen at 30 forever. TERROR_PRIME §2.11: "a
// generalization that reads a per-seat field has not generalized."
// The overlay happens here, at the one place every reader arrives, so
// every consumer — brain, engine, renderer — reads the same number.
// Shatterfield's own overlay is idempotent on top of it.
const _overlayPooledAnima = (m) => {
  if (!m || !m.sharedAnimaTeams || !m.teams || !m.players) return m;
  let changed = false;
  const players = { ...m.players };
  for (const k of Object.keys(players)) {
    const seat = players[k];
    if (!seat || typeof seat !== "object") continue;
    const t = m.teams[k];
    if (t == null) continue;
    const pooled = m.teamAnima?.[t];
    if (typeof pooled !== "number" || seat.anima === pooled) continue;
    players[k] = { ...seat, anima: pooled };
    changed = true;
  }
  return changed ? { ...m, players } : m;
};
const _r08MightBeOpen = (code) => {
  const w = _r08Watch.get(String(code));
  if (!w) return false;                       // nothing known — fail open, as before
  return isOpeningTurnIndex(w.turn, w.seatCount);
};
// Same team = not an adversary. Read inline rather than through
// matchSeats, which this module deliberately does not import (the
// dependency arrow runs the other way — see matchOutcome.js's banner).
const _sameSide = (m, a, b) => {
  if (a === b) return true;
  const ta = m?.teams?.[a], tb = m?.teams?.[b];
  return ta != null && ta === tb;
};
// ⚠ THE LIFE TOTAL A SEAT ACTUALLY HAS. Under `sharedAnimaTeams` a
// team's whole life lives at `teamAnima/{t}` and the per-seat `anima`
// field is never written, so it sits at its opening 30 for the whole
// duel. Comparing an incoming `anima` against that stale 30 is
// comparing against a number nothing reads: an attack taking a pooled
// team from 50 to 45 reads as a RISE. Same overlay matchSeats.
// withEffectiveAnima applies, done inline because this module does not
// import matchSeats (the dependency arrow runs the other way).
const _withPooledAnima = (m, idx) => {
  const seat = m?.players?.[idx];
  if (!seat) return null;
  const t = m?.teams?.[idx];
  if (t == null || !m?.sharedAnimaTeams) return seat;
  const pooled = m?.teamAnima?.[t];
  return pooled == null ? seat : { ...seat, anima: pooled };
};

// Returns a refusal string, or null to let the write through.
const _r08Refusal = async (code, playerIdx, updates, opts = {}) => {
  if (!_r08MightBeOpen(code)) return null;
  const live = await getMatch(code);
  noteBoardForR08(code, live);
  if (!live || !isOpeningTurnIndex(live.turn || 0, live.seatCount || 2)) return null;
  const actor = live.activePlayer;
  if (actor == null) return null;
  // R92 — a RESPONSE WINDOW writes to the ACTIVE seat's node, so
  // `playerIdx === actor` and this never looks. A Nihil/Omnia answers a
  // DECLARATION; it does not Attack. Stated, not left to be found.
  if (_sameSide(live, playerIdx, actor)) return null;
  const before = _withPooledAnima(live, playerIdx);
  if (!before) return null;
  const damage = adversaryDamageInPayload(before, updates);
  if (!damage) return null;
  if (opts.notAnAttack) {
    // eslint-disable-next-line no-console
    console.log(`[R08] allowed on a DECLARATION — seat ${actor} → seat ${playerIdx}: ${damage} (${opts.notAnAttack})`);
    return null;
  }
  return `[R08] REFUSED an Attack on the opening Cycle — seat ${actor} → seat ${playerIdx}: ${damage}`;
};

export const updateMatch = async (code, updates, opts = {}) => {
  if (isPlaybackLocked()) return;   // H6 — the renderer is a theatre right now
  // A multi-path update takes SLASH KEYS, so `players/3/anima` reaches
  // a seat without ever going through updatePlayer. The seal follows
  // it: the seat-shaped keys are regrouped into the per-seat payload
  // the predicate expects and asked the same question. (Today those
  // keys are Anima-ledger counters, elimination flags and R98's spend
  // stamps — none of them damage — which is exactly why nobody would
  // have noticed the day one of them was.)
  if (_r08MightBeOpen(code)) {
    const bySeat = new Map();
    for (const k of Object.keys(updates || {})) {
      const m = /^players\/(\d+)\/(.+)$/.exec(k);
      if (!m) continue;
      const idx = Number(m[1]);
      if (!bySeat.has(idx)) bySeat.set(idx, {});
      bySeat.get(idx)[m[2]] = updates[k];
    }
    for (const [idx, payload] of bySeat) {
      const refusal = await _r08Refusal(code, idx, payload, opts);
      if (refusal) { console.error(refusal); return null; }
    }
  }
  const matchRef = ref(db, `matches/${code}`);
  recordUpdate(code, null, updates);
  await update(matchRef, updates);
  noteBoardForR08(code, updates);   // a hand-off writes `turn`; keep the note current
};

// ── Update a specific player's state ─────────────────────────
export const updatePlayer = async (code, playerIdx, updates, opts = {}) => {
  if (isPlaybackLocked()) return;   // H6 — the renderer is a theatre right now
  // ── THE POOL REDIRECT (2026-09-06) ────────────────────────────────
  // Shatterfield's local shadow strips `anima` and sends it to
  // updateTeamAnima in a shared-Anima match, so the renderer never
  // reaches here with one. Every OTHER writer — the NPC brain above
  // all, plus prophecyEngine / turnEngine / bossPhases — wrote the
  // per-seat field, which nothing reads under pooling: the boss in a
  // Gauntlet — Duo could not hurt the humans and could not be hurt by
  // its own fares. The redirect lives at the chokepoint now, so the
  // brain needs no knowledge that teams exist. Read the pool note;
  // pay one read only when this code has never been seen.
  if (updates && Object.prototype.hasOwnProperty.call(updates, "anima")) {
    let note = _poolWatch.get(String(code));
    if (!note) { try { await getMatch(code); } catch (e) {} note = _poolWatch.get(String(code)); }
    const teamId = note?.shared && note.teams ? note.teams[playerIdx] : null;
    if (teamId != null) {
      const { anima, ...rest } = updates;
      await updateTeamAnima(code, teamId, anima, opts);
      if (!Object.keys(rest).length) return;
      updates = rest;
    }
  }
  const refusal = await _r08Refusal(code, playerIdx, updates, opts);
  if (refusal) { console.error(refusal); return null; }
  const playerRef = ref(db, `matches/${code}/players/${playerIdx}`);
  recordUpdate(code, `players/${playerIdx}`, updates);
  await update(playerRef, updates);
};

// ── Update a team's shared Anima pool (2v2: "they share Anima and not
// Khao" — caP'n) ────────────────────────────────────────────────
// A team's whole life total lives at one path, `teamAnima/{teamId}`,
// not duplicated per seat — so a hit either teammate takes, or a heal
// either one generates, is one write everyone reading the match sees
// the same way. Shatterfield.js's local `updatePlayer` wrapper is what
// decides WHEN to call this instead of the per-seat updatePlayer above
// (see THE FRIEND-CODE-STYLE OVERLAY comment there) — this function
// itself doesn't need to know why, only where to put the number.
export const updateTeamAnima = async (code, teamId, value, opts = {}) => {
  if (isPlaybackLocked()) return;
  if (teamId == null) return;
  // ── R08, ON THE POOLED DOOR ──────────────────────────────────────
  // In a shared-Anima 2v2 an Anima dim never reaches updatePlayer at
  // all: Shatterfield's shadow strips the `anima` key and sends it
  // HERE. Sealing updatePlayer and leaving this open would mean R08
  // held in every mode except the one where the life total moved.
  if (_r08MightBeOpen(code)) {
    const live = await getMatch(code);
    noteBoardForR08(code, live);
    if (live && isOpeningTurnIndex(live.turn || 0, live.seatCount || 2)) {
      const actor = live.activePlayer;
      const actorTeam = actor == null ? null : live.teams?.[actor];
      const was = Number(live.teamAnima?.[teamId]);
      const now = Number(value);
      const enemyPool = actor != null && (actorTeam == null || actorTeam !== teamId);
      if (enemyPool && Number.isFinite(was) && Number.isFinite(now) && now < was) {
        if (opts.notAnAttack) {
          // eslint-disable-next-line no-console
          console.log(`[R08] allowed on a DECLARATION — seat ${actor} → team ${teamId}: pooled Anima ${was} → ${now} (${opts.notAnAttack})`);
        } else {
          // eslint-disable-next-line no-console
          console.error(`[R08] REFUSED an Attack on the opening Cycle — seat ${actor} → team ${teamId}: their pooled Anima was dimmed ${was} → ${now}`);
          return null;
        }
      }
    }
  }
  const teamAnimaRef = ref(db, `matches/${code}/teamAnima/${teamId}`);
  recordUpdate(code, `teamAnima/${teamId}`, value);
  await set(teamAnimaRef, value);
};

// ── End the match ─────────────────────────────────────────────
// `winnerIdx` is a seat index (1v1, FFA) or a team id (2v2) — this
// function stores whatever it's handed; matchSeats.js's `didSeatWin`
// is what interprets either shape correctly on the read side.
//
// `runnerUp` (optional, same shape as winnerIdx — a seat or a team)
// is who finishOrEliminate was about to knock out the instant the
// match got decided — i.e. whoever placed 2nd. Nothing before H30
// ever needed a runner-up; a Tournament of Kings payout does ("2nd
// place gets some, 3rd place gets a little"), and for a 4-seat FFA
// round that produced only ONE advancing winner, "2nd in THIS match"
// is the only signal a bracket has for picking a 3rd-place entrant
// out of the six players who didn't reach the final. Absent for
// every call site that doesn't pass it — existing matches are
// unaffected.
// ── R98 · `winners` IS THE TRUTH, `winner` IS THE COMPATIBILITY ──────
// caP'n, 2026-08-25: *"both players win; ... prophecies do not prevent
// wins merely grant them"*. A Solrend can therefore end with more than
// one winner, and this function is where that becomes storage.
//
// ⚠ `winner` IS STILL WRITTEN AND IS STILL THE PRIMARY/BOARD WINNER.
// Roughly a dozen consumers — the faction tally, the Depths ELO, the
// world-map node clear, the replay commit, the bracket, the balancer's
// matchup table — read `match.winner` and expect one value. Dropping it
// would break every one of them for a ruling about a rare case. So the
// scalar stays exactly what it always was and `winners` is added
// beside it; matchSeats.didSeatWin reads BOTH and is the only thing
// most callers need.
//
// `winners` is omitted entirely for a single-winner duel rather than
// written as a one-element array, so an ordinary match's stored shape
// is byte-identical to what it was before this ruling.
export const endMatch = async (code, winnerIdx, runnerUp, winners = null) => {
  const matchRef = ref(db, `matches/${code}`);
  if (isPlaybackLocked()) return;   // H6 — the renderer is a theatre right now
  const updates = { status: "finished", winner: winnerIdx, finishedAt: Date.now() };
  if (Array.isArray(winners) && winners.length > 1) updates.winners = winners;
  if (runnerUp != null) updates.runnerUp = runnerUp;
  recordUpdate(code, null, updates);
  await update(matchRef, updates);
};

// ── THE CHAIR MOVES ONCE ────────────────────────────────────────
// ⭐ (2026-09-05) The half-turn hand-off used to be a plain update():
//
//     await updateMatch(code, { ...ledgerPaths, activePlayer: nextActive, turn: nextTurn })
//
// — no transaction, and no check that the chair was still where the
// caller thought it was. That is safe while exactly one client can ever
// call it. It stopped being safe when the disconnect grace let a NON-
// active client force-end a turn: with three or four seats, several
// clients reach the grace on the same second. In the common case they
// read the same board and write identical values, so it looks fine. But
// any client whose tick lands after the first write reads `turn: T+1`
// and writes `T+2` — advancing the chair twice and skipping a seat's
// entire turn. Shatterfield now elects ONE grace-ender, and this is the
// belt to that pair of braces: the write itself refuses to move a chair
// that has already moved.
//
// Returns { applied } — false means somebody else already did it, which
// is a normal outcome and never an error.
const setDeep = (obj, path, value) => {
  const parts = String(path).split("/");
  let node = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    // Keep an ARRAY an array when a path walks through it (2026-09-15):
    // `players/0/field/esclings/2/fallen` used to spread the rifts into
    // {0:…,1:…} — invisible on RTDB, which stores arrays as objects anyway,
    // but a headless shim hands it straight back and `.map` dies on it.
    // ⚠ AND NEVER SPREAD A SPARSE ARRAY INTO A WRITE (2026-09-16). RTDB
    // hands `tomes: {1: x, 2: y}` back as the ARRAY `[ <hole>, x, y ]`
    // (integer keys, half or more present), and `[...arr]` turns the
    // hole into an explicit `undefined` — which the SDK's validator then
    // refuses: "Data returned contains undefined in property
    // 'matches.9698.players.0.field.tomes.0'". That was a Khao Garden
    // paying its end-of-turn bump through `players/0/field/tomes/1/
    // endPaidTurn` in a live PvP duel, and the whole hand-off died.
    // Holes become `null` (what RTDB stores for "nothing here" anyway).
    node[k] = Array.isArray(node[k]) ? denseNull(node[k]) : ((node[k] && typeof node[k] === "object") ? { ...node[k] } : {});
    node = node[k];
  }
  node[parts[parts.length - 1]] = value === undefined ? null : value;
};
// A copy of an array with every hole and every `undefined` made `null`,
// so it can be handed to Firebase. Use for ANY array written whole.
export const denseNull = (arr) => Array.from(arr, (v) => (v === undefined ? null : v));

export const handOffChair = async (code, fromIdx, nextActive, nextTurn, pathUpdates = {}) => {
  const matchRef = ref(db, `matches/${code}`);
  recordUpdate(code, null, { ...pathUpdates, activePlayer: nextActive, turn: nextTurn });
  if (isPlaybackLocked()) return { applied: false };
  let applied = false;
  const res = await runTransaction(matchRef, (current) => {
    if (!current) return current;
    if (current.status === "finished") return;            // abort — the duel is over
    // THE GUARD. `fromIdx` null means the caller has no opinion (a
    // reseat after an elimination), and then only the turn number
    // protects us — which is enough, because a duplicate would carry
    // the same `nextTurn`.
    if (fromIdx != null && current.activePlayer !== fromIdx) return;   // abort — already moved
    // ⚠ STRICTLY GREATER, DELIBERATELY. `>=` looked tighter and was
    // wrong: `nextTurn` is derived from the CALLER's snapshot, which can
    // legitimately be one behind the DB, and a `>=` there would abort a
    // perfectly good hand-off and stall the table. The activePlayer guard
    // above is the one that actually closes the duplicate race — the
    // instant the first hand-off lands, the chair has moved and every
    // other client's updater aborts on that line. This is belt only.
    if (nextTurn != null && (current.turn || 0) > nextTurn) return;   // abort — already past
    const next = { ...current };
    Object.entries(pathUpdates || {}).forEach(([k, v]) => setDeep(next, k, v));
    next.activePlayer = nextActive;
    if (nextTurn != null) next.turn = nextTurn;
    applied = true;
    return next;
  });
  return { applied: !!(res.committed && applied) };
};

// ── Draw cards from grimoire ───────────────────────────────────
// drawAndDeplete — draws cards AND returns depleted grimoire
// This is the correct way to draw — always use this
export const drawAndDepleteCards = (grimoire, currentHand, count) => {
  const pool = [];
  const newGrimoire = { ...grimoire };
  Object.entries(grimoire).forEach(([cardId, qty]) => {
    for (let i = 0; i < qty; i++) pool.push(cardId);
  });
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  const drawn = pool.slice(0, count);
  drawn.forEach(id => {
    if (newGrimoire[id] > 1) newGrimoire[id]--;
    else delete newGrimoire[id];
  });
  return { hand: [...currentHand, ...drawn], grimoire: newGrimoire };
};

export const drawCards = (grimoire, currentHand, count) => {
  const pool = [];
  Object.entries(grimoire).forEach(([cardId, qty]) => {
    for (let i = 0; i < qty; i++) pool.push(cardId);
  });

  // Shuffle pool
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  const drawn = pool.slice(0, count);
  return [...currentHand, ...drawn];
};

// ── Shuffle an array ──────────────────────────────────────────
export const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};
